import { notification } from "antd";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getApiError,
    postTaskAddDebtTaskThunk,
    postTaskAddDistributionPricesThunk,
    postTaskAddRejectTaskThunk,
    postTaskAddSaleTaskThunk,
} from "tm-shared/api";
import {
    AnyTask,
    AssignmentResponseType,
    AssignmentType,
    OrderType,
    TaskRoute,
} from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";
import { v7 } from "uuid";

import { ROUTES } from "@/constants";
import { MANAGER_AGENT_VOCABULARY } from "@/constants/domain/managerAgentVocabulary";
import { ASSIGNMENT_VOCABULARY } from "@/constants/ui/assignment";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { makeCommand } from "@/hooks/useRequestQueue";
import { generateOrderId } from "@/pages/WorkPlan/utils/generateOrderId";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import { addAdditionalOfflineTask } from "@/store/slices/work";

import { isManagerRole } from "./isManagerRole";

interface UseCreateTaskArgs {
    marketId: string;
    marketTasks: AnyTask[];
}

const displayErrorMessage = (error: unknown) => {
    const { displayMessage } = getApiError(error);

    notification.error({
        message: "Не вдалося створити задачу",
        description: displayMessage,
    });
};

export const useCreateTask = ({ marketId, marketTasks }: UseCreateTaskArgs) => {
    const { offlineMode } = useAppSelector((s) => s.offline);
    const { id, role } = useAppSelector((s) => s.user);
    const { activeSchedule } = useAppSelector((s) => s.sv);
    const verifiedPerformer = isManagerRole(role) ? activeSchedule : id;
    const realRoleSlug = isManagerRole(role)
        ? MANAGER_AGENT_VOCABULARY[role]
        : role;

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [creating, setCreating] = useState<null | TaskRoute>(null);

    const createCustomTask = useCallback(async () => {
        const localTaskId = v7();

        if (offlineMode) {
            dispatch(
                addAdditionalOfflineTask({
                    id: localTaskId,
                    name: ASSIGNMENT_VOCABULARY[
                        AssignmentType.PricesDistribution
                    ].label,
                    context: {
                        route: TaskRoute.CustomTask,
                        description: "",
                        responseType: AssignmentResponseType.PhotoText,
                        files: [],
                        additionalData: {
                            assignmentType: AssignmentType.PricesDistribution,
                        },
                    },
                    completed: false,
                    targetId: marketId,
                }),
            );

            dispatch(
                enqueueRequestQueueItem({
                    id: localTaskId,
                    metadata: {
                        marketId,
                        taskId: localTaskId,
                    },
                    commands: [
                        makeCommand({
                            type: "postAddDistributionPrices",
                            title: "Створення задачі",
                            params: {
                                targetId: marketId,
                                taskId: localTaskId,
                                performer: verifiedPerformer,
                                roleSlug: realRoleSlug,
                            },
                            options: {
                                ignoreErrorCodes: ["-32010"],
                            },
                        }),
                    ],
                }),
            );

            navigate(
                buildRoutePath(ROUTES.AssignmentTask, {
                    marketId,
                    taskId: localTaskId,
                }),
            );
        } else {
            setCreating(TaskRoute.CustomTask);

            try {
                const response = await dispatch(
                    postTaskAddDistributionPricesThunk({
                        targetId: marketId,
                        taskId: localTaskId,
                        performer: verifiedPerformer,
                        roleSlug: realRoleSlug,
                    }),
                ).unwrap();

                const taskIdFromServer = response.result.id;

                navigate(
                    buildRoutePath(ROUTES.AssignmentTask, {
                        marketId,
                        taskId: taskIdFromServer,
                    }),
                );
            } catch (error) {
                displayErrorMessage(error);
            } finally {
                setCreating(null);
            }
        }
    }, [dispatch, marketId, navigate, offlineMode]);

    const createRejectTask = useCallback(async () => {
        const rejectCount = marketTasks.filter(
            (task) => task.context.route === TaskRoute.RejectProducts,
        ).length;

        let taskId = v7();
        const orderId = generateOrderId(OrderType.R, rejectCount + 1, marketId);

        if (offlineMode) {
            dispatch(
                addAdditionalOfflineTask({
                    id: taskId,
                    name: `Заявка на повернення товарів №${rejectCount + 1}`,
                    context: {
                        route: TaskRoute.RejectProducts,
                        orderId,
                    },
                    completed: false,
                    targetId: marketId,
                }),
            );

            dispatch(
                enqueueRequestQueueItem({
                    id: taskId,
                    metadata: {
                        marketId,
                        taskId: taskId,
                        orderId,
                    },
                    commands: [
                        makeCommand({
                            type: "postOrderCreate",
                            title: "Створення заявки",
                            params: {
                                type: OrderType.R,
                                marketId,
                                orderId,
                            },
                            options: {
                                ignoreErrorCodes: ["-32000"],
                            },
                        }),
                        makeCommand({
                            type: "postTaskAddSaleTask",
                            title: "Створення задачі",
                            params: {
                                targetId: marketId,
                                taskId,
                                orderId,
                                performer: verifiedPerformer,
                            },
                            options: {
                                ignoreErrorCodes: ["-32010"],
                            },
                        }),
                    ],
                }),
            );
        } else {
            setCreating(TaskRoute.RejectProducts);

            try {
                const { result } = await dispatch(
                    postTaskAddRejectTaskThunk({
                        targetId: marketId,
                        performer: verifiedPerformer,
                    }),
                ).unwrap();

                taskId = result.id;
            } catch (error) {
                displayErrorMessage(error);

                return;
            } finally {
                setCreating(null);
            }
        }

        navigate(
            buildRoutePath(ROUTES.RejectProductsTask, {
                marketId,
                taskId,
            }),
        );
    }, [dispatch, marketId, marketTasks, navigate, offlineMode]);

    const createAdditionalSale = async () => {
        const salesCount = marketTasks.filter(
            (task) => task.context.route === TaskRoute.SaleAdditional,
        ).length;

        const localTaskId = v7();
        const orderId = generateOrderId(OrderType.H, salesCount + 1, marketId);

        if (offlineMode) {
            dispatch(
                addAdditionalOfflineTask({
                    id: localTaskId,
                    name: `Продаж №${salesCount + 1}`,
                    context: {
                        route: TaskRoute.SaleAdditional,
                        orderId,
                    },
                    completed: false,
                    targetId: marketId,
                }),
            );

            dispatch(
                enqueueRequestQueueItem({
                    id: localTaskId,
                    metadata: {
                        marketId,
                        taskId: localTaskId,
                        orderId,
                    },
                    commands: [
                        makeCommand({
                            type: "postOrderCreate",
                            title: "Створення заявки",
                            params: {
                                marketId,
                                orderId,
                                type: OrderType.H,
                            },
                            options: {
                                ignoreErrorCodes: ["-32000"],
                            },
                        }),
                        makeCommand({
                            type: "postTaskAddSaleTask",
                            title: "Створення задачі",
                            params: {
                                targetId: marketId,
                                taskId: localTaskId,
                                orderId,
                                performer: verifiedPerformer,
                            },
                            options: {
                                ignoreErrorCodes: ["-32010"],
                            },
                        }),
                    ],
                }),
            );
        } else {
            try {
                setCreating(TaskRoute.SaleAdditional);

                await dispatch(
                    postTaskAddSaleTaskThunk({
                        targetId: marketId,
                        performer: verifiedPerformer,
                    }),
                ).unwrap();
            } catch (error) {
                displayErrorMessage(error);
            } finally {
                setCreating(null);
            }
        }
    };

    const createTakeDebtTask = useCallback(async () => {
        const localTaskId = v7();

        if (offlineMode) {
            dispatch(
                addAdditionalOfflineTask({
                    id: localTaskId,
                    name: "Забрати борг",
                    context: {
                        route: TaskRoute.TakeDebt,
                    },
                    completed: false,
                    targetId: marketId,
                }),
            );

            dispatch(
                enqueueRequestQueueItem({
                    id: localTaskId,
                    metadata: {
                        marketId,
                        taskId: localTaskId,
                    },
                    options: {
                        todayOnly: true,
                    },
                    commands: [
                        makeCommand({
                            type: "postTaskAddDebtTask",
                            title: "Створення задачі",
                            params: {
                                targetId: marketId,
                                taskId: localTaskId,
                                performer: verifiedPerformer,
                            },
                            options: {
                                ignoreErrorCodes: ["-32010"],
                            },
                        }),
                    ],
                }),
            );

            navigate(
                buildRoutePath(ROUTES.TakeDebtTask, {
                    marketId,
                    taskId: localTaskId,
                }),
            );
        } else {
            setCreating(TaskRoute.TakeDebt);

            try {
                const response = await dispatch(
                    postTaskAddDebtTaskThunk({
                        targetId: marketId,
                        taskId: localTaskId,
                        performer: verifiedPerformer,
                    }),
                ).unwrap();

                const taskIdFromServer = response.result.id;

                navigate(
                    buildRoutePath(ROUTES.TakeDebtTask, {
                        marketId,
                        taskId: taskIdFromServer,
                    }),
                );
            } catch (error) {
                displayErrorMessage(error);
            } finally {
                setCreating(null);
            }
        }
    }, [dispatch, marketId, navigate, offlineMode]);

    return {
        createAdditionalSale,
        createTakeDebtTask,
        createCustomTask,
        createRejectTask,
        creating,
    };
};
